import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CardContent,
  DataTable,
  EmptyState,
  FullscreenSpinner,
  Input,
  type Column,
} from '@shared/ui';
import {
  userApi,
  type AdminUserSearchView,
  type KycStatus,
  type UserRole,
} from '@entities/user';
import { formatDateTime } from '@shared/lib/format';
import { ROUTES } from '@shared/config/routes';

const kycVariant: Record<KycStatus, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  NONE: 'secondary',
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'destructive',
};

const COLUMNS: Column<AdminUserSearchView>[] = [
  {
    key: 'email',
    header: 'Email',
    cell: (r) => (
      <Link to={ROUTES.userDetail(r.id)} className="font-medium text-primary hover:underline">
        {r.email}
      </Link>
    ),
  },
  { key: 'name', header: 'Имя', cell: (r) => r.displayName ?? '—' },
  { key: 'phone', header: 'Phone', cell: (r) => r.phone ?? '—' },
  {
    key: 'roles',
    header: 'Роли',
    cell: (r) => (
      <div className="flex flex-wrap gap-1">
        {r.roles.map((role) => (
          <Badge key={role} variant="outline" className="font-mono text-[10px]">
            {role}
          </Badge>
        ))}
      </div>
    ),
  },
  { key: 'kyc', header: 'KYC', cell: (r) => <Badge variant={kycVariant[r.kycStatus]}>{r.kycStatus}</Badge> },
  {
    key: 'blocked',
    header: 'Статус',
    cell: (r) =>
      r.blocked ? <Badge variant="destructive">blocked</Badge> : <Badge variant="success">active</Badge>,
  },
  { key: 'created', header: 'Создан', cell: (r) => formatDateTime(r.createdAt) },
];

export function UsersListPage() {
  const [params, setParams] = useSearchParams();

  const email = params.get('email') ?? '';
  const phone = params.get('phone') ?? '';
  const role = (params.get('role') as UserRole | null) ?? '';
  const blockedRaw = params.get('blocked');
  const kyc = (params.get('kyc') as KycStatus | null) ?? '';
  const q = params.get('q') ?? '';

  const blocked = blockedRaw === 'true' ? true : blockedRaw === 'false' ? false : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['users', { email, phone, role, blocked, kyc, q }],
    queryFn: () =>
      userApi.search({
        email: email || undefined,
        phone: phone || undefined,
        role: (role || undefined) as UserRole | undefined,
        blocked,
        kyc: (kyc || undefined) as KycStatus | undefined,
        q: q || undefined,
        size: 50,
      }),
  });

  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Пользователи</h1>

      <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-6">
        <Input placeholder="Поиск (email/имя)" defaultValue={q} onBlur={(e) => update('q', e.target.value)} />
        <Input placeholder="email" defaultValue={email} onBlur={(e) => update('email', e.target.value)} />
        <Input placeholder="phone" defaultValue={phone} onBlur={(e) => update('phone', e.target.value)} />
        <select
          value={role}
          onChange={(e) => update('role', e.target.value || null)}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Все роли</option>
          <option value="BUYER">BUYER</option>
          <option value="PRODUCER">PRODUCER</option>
          <option value="ADMIN">ADMIN</option>
        </select>
        <select
          value={kyc}
          onChange={(e) => update('kyc', e.target.value || null)}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Любой KYC</option>
          <option value="NONE">NONE</option>
          <option value="PENDING">PENDING</option>
          <option value="VERIFIED">VERIFIED</option>
          <option value="REJECTED">REJECTED</option>
        </select>
        <select
          value={blockedRaw ?? ''}
          onChange={(e) => update('blocked', e.target.value || null)}
          className="h-10 rounded-md border bg-background px-2 text-sm"
        >
          <option value="">Все</option>
          <option value="false">Активные</option>
          <option value="true">Заблокированные</option>
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <FullscreenSpinner />
          ) : (
            <DataTable
              rows={data?.content ?? []}
              columns={COLUMNS}
              rowKey={(r) => r.id}
              empty={<EmptyState title="Пользователей не найдено" />}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
